import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import {
  CompanyCompositeEntity,
  CompanyEntity,
  CompanyFlagHistoryCompositeEntity,
  CompanyFlagHistoryCompositeStubEntity,
  CompanyShareClassOwnershipCompositeEntity,
  CompanyShareClassOwnershipEntity,
  CompanyShareOwnershipBeneficialCompositeEntity,
  CompanyShareOwnershipDirectCompositeEntity,
  CompanyShareOwnershipDirectEntity,
  CompanyShareOwnershipEntity,
  CompanyShareOwnershipIndirectCompositeEntity,
  CompanyShareOwnershipIndirectEntity,
  CompanyShareOwnershipUltimateCompositeEntity,
  CompanyShareOwnershipUltimateEntity,
  OwnershipPathsComposite,
  SimpleCollection,
  UnifiedOwnershipCompositeEntity,
  isCompanyShareClassOwnershipShallowComposite,
  isCompanyShareOwnershipBeneficialCompositeEntity,
  isCompanyShareOwnershipDirectCompositeEntity,
  isCompanyShareOwnershipIndirectCompositeEntity,
  isCompanyShareOwnershipUltimateCompositeEntity,
} from '@nk/data-entities-lib';

export interface OrgNrThing {
  orgNr: string;
  orgNrSchema: string | null;
  valid: boolean;
}

export function getOrgNr(orgNr: string): OrgNrThing {
  // Should pass user preference
  const orgNrThing: OrgNrThing = {
    orgNr: orgNr.replace(/\s/g, '').toUpperCase(), // regex expression removes space,
    orgNrSchema: null,
    valid: false,
  };
  const validOrgNrSchema = ['NO', 'SE', 'FI', 'DK'];
  // TODO later: Map long form country names to short form, enabling handling orgNrs with long form names as "prefix".
  //  const validOrgNrSchemaLongForm = ['Norway', 'Sweden', 'Finland', 'Denmark'];
  const possibleSchema = orgNrThing.orgNr.substring(0, 2);
  let foundSchemaInBeginning = false;
  if (validOrgNrSchema.includes(possibleSchema)) {
    if (orgNrThing.orgNr.substring(0, 2) === orgNrThing.orgNr.substring(2, 4)) {
      // Checks if orgNr has double country code
      orgNrThing.orgNr = orgNrThing.orgNr.slice(4);
    }
    foundSchemaInBeginning = true;
    orgNrThing.orgNrSchema = possibleSchema;
    orgNrThing.orgNr = orgNrThing.orgNr.substring(2);
  }
  // Find the org_nr schema.
  if (orgNrThing.orgNr.includes('-')) {
    const parts = orgNrThing.orgNr.split('-');
    if (parts[0].length === 6 && parts[1].length === 4) {
      // Swedish 000000-0000
      if (foundSchemaInBeginning && possibleSchema !== 'SE') {
        orgNrThing.valid = false;
      } else {
        orgNrThing.orgNrSchema = 'SE';
        orgNrThing.valid = true;
      }
    } else if (parts[0].length === 7 && parts[1].length === 1) {
      // Finnish 0000000-0
      if (foundSchemaInBeginning && possibleSchema !== 'FI') {
        orgNrThing.valid = false;
      } else {
        orgNrThing.orgNrSchema = 'FI';
        orgNrThing.valid = true;
      }
    }
  } else {
    const isOnlyNumbers = /^\d+$/.test(orgNrThing.orgNr);
    if (!isOnlyNumbers) {
      orgNrThing.valid = false;
    } else if (orgNrThing.orgNr.length === 9) {
      // Norwegian - NO000000000
      if (foundSchemaInBeginning && possibleSchema !== 'NO') {
        orgNrThing.valid = false;
      } else {
        orgNrThing.orgNrSchema = 'NO';
        orgNrThing.valid = true;
      }
    } else if (orgNrThing.orgNr.length === 8) {
      // Danish - DK00000000
      if (foundSchemaInBeginning && possibleSchema !== 'DK') {
        orgNrThing.valid = false;
      } else {
        orgNrThing.orgNrSchema = 'DK';
        orgNrThing.valid = true;
      }
    } else {
      orgNrThing.orgNrSchema = null;
    }
  }

  const isOnlyLetters = orgNrThing.orgNr.match('^[a-zA-Z]+$');
  if (isOnlyLetters || orgNrThing.orgNr.length < 4 || orgNrThing.orgNr.includes('.')) {
    orgNrThing.valid = false;
  }
  return orgNrThing;
}

export function formatOrgNr(orgNr: string, orgNrSchema: string): string {
  const possibleOrgNrSchema = ['NO', 'SE', 'FI', 'DK'];
  orgNr = orgNr.replace(/\s/g, ''); // remove space
  orgNrSchema = orgNrSchema.replace(/\s/g, ''); // remove space

  if (orgNrSchema.length > 2 && possibleOrgNrSchema.includes(orgNrSchema)) {
    console.error(
      'Error in PIPE: Org. nr. Schema should be 2 letters, and be one of: ' + possibleOrgNrSchema.join(','),
    );
    return orgNrSchema + orgNr;
  }
  if (orgNrSchema === 'NO') {
    return `${orgNrSchema} ${orgNr.substring(0, 3)} ${orgNr.substring(3, 6)} ${orgNr.substring(6, 9)}`;
  } else if (['SE', 'DK', 'FI'].includes(orgNrSchema)) {
    return `${orgNrSchema} ${orgNr}`;
  } else {
    return orgNrSchema + orgNr;
  }
}

export function hasDeletedFlag(companyBasicComposite: CompanyCompositeEntity): boolean {
  return companyBasicComposite.company_flag_history_composites.some(
    (composite) => composite.company_flag_type.company_flag_type_key === 'confirmed_dead',
  );
}

export function hasRegisteredBankruptFlag(
  companyBasicComposite: CompanyCompositeEntity,
): CompanyFlagHistoryCompositeStubEntity | null {
  return (
    companyBasicComposite.company_flag_history_composites.find(
      (composite) => composite.company_flag_type.company_flag_type_key === 'registered_bankrupt',
    ) ?? null
  );
}

export function hasUnderLiquidationFlag(
  companyBasicComposite: CompanyCompositeEntity,
): CompanyFlagHistoryCompositeStubEntity | null {
  return (
    companyBasicComposite.company_flag_history_composites.find(
      (composite) => composite.company_flag_type.company_flag_type_key === 'registered_liquidation',
    ) ?? null
  );
}

export function hasUnderForcedLiquidationFlag(
  companyBasicComposite: CompanyCompositeEntity,
): CompanyFlagHistoryCompositeStubEntity | null {
  return (
    companyBasicComposite.company_flag_history_composites.find(
      (composite) => composite.company_flag_type.company_flag_type_key === 'registered_forced_liquidation',
    ) ?? null
  );
}

export enum ShareType {
  AnyShares = 'any-shares',
  VotingShares = 'voting-shares',
}

export const SHARE_TYPES = [
  { key: ShareType.AnyShares, label: extractTranslation('company_ownership.any_shares') },
  { key: ShareType.VotingShares, label: extractTranslation('company_ownership.only_voting_rights') },
] as const;

export const DEFAULT_SHARE_TYPE = SHARE_TYPES[0].key;

export enum OwnershipViews {
  Direct = 'DIRECT',
  Classes = 'CLASSES',
  Count = 'COUNT',
  Ultimate = 'ULTIMATE',
  Indirect = 'INDIRECT',
  Beneficial = 'BENEFICIAL', // Only when fetching shareholders
}

export interface CompanyOwnershipTableInputParams {
  showByClass: boolean;
  dataSourceKey: string;
  viewType: OwnershipViews;
  shareIssuerCompanyIdentifier: string;
  shareholderCompanyIdentifier: string;
  selectedYear: number;
}

export interface CompanyOwnershipTableParams extends CompanyOwnershipTableInputParams {
  shareType: ShareType;
  showClassesAndCount: boolean;
  page: number;
  limit: number;
}

export interface OwnershipData {
  data: CompanyShareOwnershipCommonComposite[];
  totalCount: number;
  viewType: OwnershipViews;
  shareType: ShareType;
  shareIssuerCompanyUuidInput?: string;
  shareholderCompanyUuidInput?: string;
}

export type CompanyShareOwnershipCommonComposite =
  | CompanyShareOwnershipDirectCompositeEntity
  | CompanyShareClassOwnershipCompositeEntity
  | CompanyShareOwnershipUltimateCompositeEntity
  | CompanyShareOwnershipIndirectCompositeEntity
  | CompanyShareOwnershipBeneficialCompositeEntity;

export function mapOwnershipData(
  payload: SimpleCollection<CompanyShareOwnershipCommonComposite>,
  ownershipParams: CompanyOwnershipTableParams,
): SimpleCollection<UnifiedOwnershipCompositeEntity> | null {
  if (payload?.elements) {
    return {
      count: payload.count,
      elements: payload.elements.map((el) =>
        convertToSimpleOwnership(
          ownershipParams.shareIssuerCompanyIdentifier,
          ownershipParams.shareholderCompanyIdentifier,
          el,
        ),
      ),
      has_more: payload.has_more,
    };
  } else {
    return null;
  }
}

export function singleOwnership(
  data: CompanyShareOwnershipCommonComposite,
):
  | CompanyShareOwnershipUltimateEntity
  | CompanyShareOwnershipIndirectEntity
  | CompanyShareOwnershipDirectEntity
  | CompanyShareOwnershipEntity
  | CompanyShareClassOwnershipEntity
  | null {
  let ownership = null;
  if (isCompanyShareOwnershipUltimateCompositeEntity(data)) {
    ownership = data.company_share_ownership_ultimate;
  } else if (isCompanyShareOwnershipIndirectCompositeEntity(data)) {
    ownership = data.company_share_ownership_indirect;
  } else if (isCompanyShareOwnershipBeneficialCompositeEntity(data)) {
    ownership = data.company_share_ownership_beneficial;
  } else if (isCompanyShareOwnershipDirectCompositeEntity(data)) {
    ownership = data.company_share_ownership_direct;
  } else if (isCompanyShareClassOwnershipShallowComposite(data)) {
    ownership = data.company_share_class_ownership;
  } else {
    console.error('missing ownership data', data);
  }
  return ownership;
}

export function convertToSimpleOwnership(
  shareIssuerCompanyUuidInput: string | undefined,
  shareholderCompanyUuidInput: string | undefined,
  data: CompanyShareOwnershipCommonComposite,
): UnifiedOwnershipCompositeEntity {
  let company: CompanyEntity | null = null;
  let companyFlagHistoryComposites: CompanyFlagHistoryCompositeEntity[] | null = null;
  if (shareIssuerCompanyUuidInput) {
    company = data.shareholder_company;
    companyFlagHistoryComposites = data.shareholder_company_flag_history_composites;
  } else if (shareholderCompanyUuidInput && 'company' in data) {
    company = data.company;
    companyFlagHistoryComposites = data.share_issuer_company_flag_history_composites;
  }

  let shareClassGroupKey: string | null = null;
  let shareClassKey: string | null = null;
  let sharePercentByClass: number | null = null;
  let sharePercentByTotal: number | null = null;
  let ownershipUpper: number | null = null;
  let ownershipLower: number | null = null;
  let ownershipPathsComposite: OwnershipPathsComposite | null = null;

  let votingOwnership: number | null = null;

  const ownership = singleOwnership(data);

  if (isCompanyShareOwnershipDirectCompositeEntity(data)) {
    votingOwnership = data.company_share_ownership_direct.voting_ownership;
    // TODO: make ownership upper and lower into a single ownership?
    ownershipUpper = data.company_share_ownership_direct.ownership_upper;
    ownershipLower = data.company_share_ownership_direct.ownership_lower;
  }
  if (isCompanyShareClassOwnershipShallowComposite(data)) {
    votingOwnership = data.company_share_class_ownership.voting_ownership;
    shareClassGroupKey = data.share_class.share_class_group_key;
    shareClassKey = data.share_class.share_class_key;
    const companyShareClassOwnershipShareCount = data.company_share_class_ownership.share_count || 0;
    const companyShareClassOwnershipTotalShareCount = data.company_share_class_ownership.total_share_count || 0;
    if (data.company_share_class_aggregate.share_count) {
      sharePercentByClass = companyShareClassOwnershipShareCount / data.company_share_class_aggregate.share_count;
    } else {
      sharePercentByClass = null;
    }
    sharePercentByTotal = companyShareClassOwnershipShareCount / companyShareClassOwnershipTotalShareCount;
  }
  if (isCompanyShareOwnershipBeneficialCompositeEntity(data)) {
    ownershipPathsComposite = data.ownership_paths_composite;
    votingOwnership = data.company_share_ownership_beneficial.voting_ownership;
  }

  return {
    person: data.shareholder_person && data.shareholder_person.uuid ? data.shareholder_person : null,
    company: company && company.uuid ? company : null,
    personFlagComposites: data.shareholder_person_flag_composites,
    ownership: ownership ? ownership.ownership : null,
    shareholderName: ownership ? ownership.shareholder_name : null,
    votingOwnership: votingOwnership,
    ownershipUpper: ownershipUpper,
    ownershipLower: ownershipLower,
    companyFlagHistoryComposites: companyFlagHistoryComposites,
    ownershipPathsComposite: ownershipPathsComposite,
    sharePercentByClass: sharePercentByClass,
    sharePercentByTotal: sharePercentByTotal,
    shareClassGroupKey: shareClassGroupKey,
    shareClassKey: shareClassKey,
    data: ownership,
  };
}
